import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import TicketExpenses from '../TicketExpenses';
import TicketAppointments from '../TicketAppointments';
import WorkOrderForm from '../WorkOrderForm';
import TicketDialog from './TicketDialog';
import { hrs, statusOf } from './ticketView';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const today = () => new Date().toISOString().slice(0, 10);
const day = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const statusWord = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * WORK & COSTS: the ticket's work order, its hours against the budget, what
 * was spent and the visits - one place, for the office and for whoever does
 * the job (Gordon's decision 2: the work order IS this tab, and
 * /work-orders/:id brings you here).
 *
 * Everything reads and writes the endpoints the work order page and the
 * contractor page used, so nothing about who may log, claim or arrange
 * changes: GET /work-orders/:id and GET /tickets/:id/time for the figures,
 * POST /work-orders/:id/hours to log, POST /work-orders/:id/cancel to call
 * the job off, PUT /work-orders/:id (the edit form) for an administrator.
 */
export default function WorkTab() {
  const ctx = useOutletContext();
  const { ticket, summary, role, can, auth, id, locked, reload, setNotice, contractorMode } = ctx;
  const location = useLocation();
  const [wo, setWo] = useState(undefined);       // undefined loading, null none
  const [sheet, setSheet] = useState(null);
  const [problem, setProblem] = useState(null);
  const [dialog, setDialog] = useState(null);    // 'calloff' | 'edit' | 'raise'
  const [attachments, setAttachments] = useState([]);
  const hoursBox = useRef(null);

  const woId = summary?.work?.work_order?.id || null;

  const load = useCallback(async () => {
    setProblem(null);
    let time = null;
    try {
      const t = await axios.get(`${API_BASE}/tickets/${id}/time`, auth);
      time = t.data && typeof t.data === 'object' && !Array.isArray(t.data) ? t.data : null;
    } catch {
      time = null;
    }
    setSheet(time);
    // The work order: the summary names it; failing that, the timesheet does
    // (GET /tickets/:id/time carries work_order_id); failing that, a
    // contractor finds their own the way the old page did.
    let found = woId || time?.work_order_id || null;
    let listed = null;
    if (contractorMode && !woId) {
      try {
        const w = await axios.get(`${API_BASE}/my-work-orders`, auth);
        listed = (w.data?.workOrders || []).find((o) => String(o.ticket_id) === String(id)) || null;
        if (!found) found = listed?.id || null;
      } catch { listed = null; }
    }
    // What is known without the work order's own read, should it fail: the
    // contractor's list row, else the summary and the timesheet.
    const known = found ? {
      id: found,
      work_order_number: listed?.work_order_number || summary?.work?.work_order?.number || time?.work_order_number || null,
      status: listed?.status || summary?.work?.work_order?.status || 'in_progress',
      allocated_hours: listed?.allocated_hours ?? summary?.work?.allocated_hours ?? time?.allocated_hours ?? 0,
      description: listed?.description || null
    } : null;
    if (found) {
      try {
        const r = await axios.get(`${API_BASE}/work-orders/${found}`, auth);
        const ok = r.data && typeof r.data === 'object' && !Array.isArray(r.data) && r.data.id;
        setWo(ok ? r.data : known);
      } catch {
        setWo(known);
      }
    } else {
      setWo(null);
    }
    try {
      const a = await axios.get(`${API_BASE}/tickets/${id}/attachments`, auth);
      setAttachments(Array.isArray(a.data) ? a.data : []);
    } catch { setAttachments([]); }
  }, [woId, contractorMode, auth, id, summary]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (location.state?.logTime && wo) setTimeout(() => hoursBox.current?.focus(), 30);
  }, [location.state, wo]);

  const refresh = async () => { await load(); await reload(); };

  if (statusOf(ticket) === 'awaiting approval') {
    return (
      <div className="tk-work">
        <p className="tk-empty">Nothing is spent before approval. The budget set on approval appears here.</p>
      </div>
    );
  }

  const entries = Array.isArray(sheet?.entries) ? sheet.entries : [];
  const logged = sheet && sheet.logged_hours != null ? Number(sheet.logged_hours)
    : entries.reduce((n, e) => n + Number(e.hours_logged || 0), 0);
  const allocated = Number(wo?.allocated_hours || sheet?.allocated_hours || 0);
  const noBudget = !(allocated > 0);
  const pct = noBudget ? 0 : (logged / allocated) * 100;
  const over = !noBudget && logged > allocated;
  const open = wo && !['completed', 'cancelled'].includes(String(wo.status || ''));
  // A finished or called-off job takes no more claims or visits, as the work
  // order page had it: the figure is agreed.
  const closedJob = Boolean(wo) && !open;
  const mayLog = can.canWork && !locked && open && role !== 'raiser'
    && statusOf(ticket) !== 'resolved' && sheet?.writable !== false;

  return (
    <div className="tk-work">
      {problem && <p className="tk-banner" data-tone="red" role="alert">{problem}</p>}

      <section className="tk-card tk-wo" aria-labelledby="tk-wo-h">
        <div className="tk-card-head">
          <h2 className="tk-card-title" id="tk-wo-h">
            Work order{wo?.work_order_number ? <span className="tk-mono"> {wo.work_order_number}</span> : null}
          </h2>
          {wo && <span className="tk-chip" data-tone={wo.status === 'cancelled' ? 'grey' : wo.status === 'completed' ? 'green' : 'blue'}>{statusWord(wo.status)}</span>}
          <span className="tk-spacer" />
          {role === 'admin' && wo && (
            <>
              <button type="button" className="tk-btn tk-btn-small" onClick={() => setDialog('edit')}>Edit</button>
              {open && <button type="button" className="tk-btn tk-btn-small tk-btn-danger-quiet" data-action="call-off"
                               onClick={() => setDialog('calloff')}>Call off</button>}
            </>
          )}
        </div>

        {wo === undefined ? <p className="tk-quiet">Loading…</p> : !wo ? (
          <div className="tk-wo-none">
            <p className="tk-quiet">
              {role === 'admin'
                ? 'No work order yet, so hours can’t be logged.'
                : 'There is no work order against this ticket yet, so there is nowhere to log hours. The office raises one.'}
            </p>
            {role === 'admin' && ticket.contractor_id && (
              <button type="button" className="tk-btn tk-btn-primary" onClick={() => setDialog('raise')}>Raise a work order</button>
            )}
          </div>
        ) : (
          <>
            {wo.description && <p className="tk-wo-desc">{wo.description}</p>}
            <dl className="tk-wo-hours">
              <div><dt>Budget</dt><dd>{noBudget ? 'None' : hrs(allocated)}</dd></div>
              <div><dt>Logged</dt><dd>{hrs(logged)}</dd></div>
              <div data-over={over || undefined}><dt>Remaining</dt><dd>{noBudget ? '—' : hrs(allocated - logged)}</dd></div>
              <div><dt>Used</dt><dd>{noBudget ? '—' : `${Math.round(pct)}%`}</dd></div>
            </dl>
            <div className="tk-bar tk-bar-big" data-state={noBudget ? 'none' : over ? 'over' : pct > 90 ? 'tight' : 'ok'}
                 role="img" aria-label={noBudget ? `${hrs(logged)} logged against no budget` : `${Math.round(pct)}% of the budget used`}>
              <span style={{ width: `${noBudget ? 0 : Math.min(100, pct)}%` }} />
            </div>
            <dl className="tk-facts tk-facts-inline">
              {wo.contractor_name && <div className="tk-fact"><dt>Doing it</dt><dd>{wo.contractor_name}</dd></div>}
              {role !== 'contractor' && wo.hourly_rate != null && wo.hourly_rate !== '' && Number(wo.hourly_rate) > 0 && (
                <div className="tk-fact"><dt>Rate</dt><dd>€{Number(wo.hourly_rate).toFixed(2)}/hr</dd></div>
              )}
              {wo.start_date && <div className="tk-fact"><dt>Starts</dt><dd>{day(wo.start_date)}</dd></div>}
              {wo.end_date && <div className="tk-fact"><dt>Ends</dt><dd>{day(wo.end_date)}</dd></div>}
            </dl>
            {sheet?.locked_reason && <p className="tk-banner" data-tone="grey">{sheet.locked_reason}</p>}
          </>
        )}
      </section>

      {wo && (
        <section className="tk-card" aria-labelledby="tk-hours-h">
          <h2 className="tk-card-title" id="tk-hours-h">Hours</h2>
          {mayLog && <LogTime wo={wo} auth={auth} boxRef={hoursBox} onDone={async () => { setNotice('Time logged.'); await refresh(); }} setProblem={setProblem} />}
          {entries.length === 0 ? (
            <p className="tk-quiet">Nothing logged against this job yet.</p>
          ) : (
            <>
              <ol className="tk-log">
                {entries.map((e) => (
                  <li className="tk-log-row" key={e.id}>
                    <span className="tk-log-when">{day(e.date_worked)}</span>
                    <span className="tk-log-what">
                      {e.description || <em>No note</em>}
                      {e.logged_by_name && <span className="tk-quiet"> — {e.logged_by_name}</span>}
                    </span>
                    <span className="tk-log-hours">{hrs(e.hours_logged)}</span>
                  </li>
                ))}
              </ol>
              <div className="tk-log-total"><span>Total logged</span><span>{hrs(logged)}</span></div>
            </>
          )}
        </section>
      )}

      <section className="tk-card" aria-labelledby="tk-exp-h">
        <h2 className="tk-card-title" id="tk-exp-h">Expenses</h2>
        <TicketExpenses ticketId={id} auth={auth} canClaim={!locked && !closedJob && role !== 'raiser'}
                        attachments={attachments} onChanged={() => ctx.loadSummary()} />
      </section>

      {/* The appointments panel draws its own heading and its actions. */}
      <section className="tk-card" aria-label="Visits">
        <TicketAppointments ticketId={id} auth={auth} canArrange={!locked && !closedJob} onChanged={() => ctx.loadSummary()} />
      </section>

      {dialog === 'calloff' && wo && (
        <CallOff wo={wo} onClose={() => setDialog(null)} onDone={async () => { setDialog(null); setNotice('The job was called off.'); await refresh(); }} />
      )}
      {dialog === 'edit' && wo && (
        <TicketDialog label={`Edit ${wo.work_order_number || 'work order'}`} wide onClose={() => setDialog(null)}>
          <WorkOrderForm workOrderId={wo.id} workOrderData={wo} isAdmin isReadOnly={false} startEditing
                         onSave={async () => { setDialog(null); setNotice('Work order saved.'); await refresh(); }} />
        </TicketDialog>
      )}
      {dialog === 'raise' && (
        <RaiseWorkOrder ticket={ticket} onClose={() => setDialog(null)}
                        onDone={async () => { setDialog(null); setNotice('Work order raised.'); await refresh(); }} />
      )}
    </div>
  );
}

function LogTime({ wo, auth, boxRef, onDone, setProblem }) {
  const [date, setDate] = useState(today);
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!Number(hours)) return;
    setBusy(true);
    setProblem(null);
    try {
      await axios.post(`${API_BASE}/work-orders/${wo.id}/hours`, {
        hours_logged: Number(hours), date_worked: date, description: note.trim() || null
      }, auth);
      setHours('');
      setNote('');
      await onDone();
    } catch (err) {
      setProblem(err.response?.data?.error || 'That time did not save.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="tk-logform" onSubmit={submit} aria-label="Log time">
      <label className="tk-logfield">
        <span className="tk-label">Date</span>
        <input type="date" className="tk-input" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      <label className="tk-logfield tk-logfield-hours">
        <span className="tk-label">Hours</span>
        <input ref={boxRef} type="text" inputMode="decimal" className="tk-input" placeholder="2.5" value={hours}
               onChange={(e) => setHours(e.target.value.replace(/[^0-9.]/g, ''))} data-field="log-hours" />
      </label>
      <label className="tk-logfield tk-grow">
        <span className="tk-label">What you did</span>
        <input type="text" className="tk-input" value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <button type="submit" className="tk-btn tk-btn-primary" disabled={busy || !Number(hours)}>
        {busy ? 'Saving…' : 'Log it'}
      </button>
    </form>
  );
}

function CallOff({ wo, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const go = async () => {
    if (!reason.trim()) { setError('Say why the job was called off'); return; }
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/work-orders/${wo.id}/cancel`, { reason: reason.trim() });
      await onDone();
    } catch (err) {
      const b = err.response?.data;
      setError(b?.error ? (b.detail ? `${b.error}. ${b.detail}` : b.error) : 'Could not call that job off');
    } finally {
      setBusy(false);
    }
  };
  return (
    <TicketDialog label={`Call off ${wo.work_order_number || 'the job'}?`} onClose={() => { if (!busy) onClose(); }}>
      <p className="tk-quiet">
        The job is marked cancelled and the reason is kept on it and on the ticket’s history. A job with hours logged against it cannot be called off.
      </p>
      <label className="tk-label" htmlFor="tk-calloff">Why is it being called off?</label>
      <textarea id="tk-calloff" className="tk-input" rows={3} value={reason} disabled={busy}
                onChange={(e) => setReason(e.target.value)} placeholder="e.g. The client withdrew the request" />
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="tk-row tk-row-end">
        <button type="button" className="tk-btn tk-btn-quiet" disabled={busy} onClick={onClose}>Keep it</button>
        <button type="button" className="tk-btn tk-btn-danger" disabled={busy || !reason.trim()} onClick={go}>
          {busy ? 'Calling off…' : 'Call off the job'}
        </button>
      </div>
    </TicketDialog>
  );
}

function RaiseWorkOrder({ ticket, onClose, onDone }) {
  const [budget, setBudget] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/work-orders`, {
        ticket_id: ticket.id, contractor_id: ticket.contractor_id,
        description: ticket.title, allocated_hours: budget.trim() === '' ? null : budget.trim()
      });
      await onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not raise the work order');
    } finally {
      setBusy(false);
    }
  };
  return (
    <TicketDialog label="Raise a work order" onClose={() => { if (!busy) onClose(); }}>
      <p className="tk-quiet">For {ticket.contractor_name || 'the contractor on this ticket'}, so hours can be logged against it.</p>
      <label className="tk-label" htmlFor="tk-raise-budget">Hour budget (optional)</label>
      <input id="tk-raise-budget" className="tk-input" type="text" inputMode="decimal" value={budget}
             onChange={(e) => setBudget(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="e.g. 6" />
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="tk-row tk-row-end">
        <button type="button" className="tk-btn tk-btn-quiet" disabled={busy} onClick={onClose}>Cancel</button>
        <button type="button" className="tk-btn tk-btn-primary" disabled={busy} onClick={go}>{busy ? 'Raising…' : 'Raise it'}</button>
      </div>
    </TicketDialog>
  );
}
